import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { profileActions } from "../../../di/di";
import { changePasswordActions } from "../../../di/di";
import moment from "moment";
import VideoChatProgressComponent from "./video_chat_progress_component";

export default function ProfileSubsDetailsComponent({ showCancelTour = false }) {
  const dispatch = useDispatch();
  const userCurrentTier = useSelector((state) => state.profile.user?.tierId);
  const user = useSelector((state) => state.profile.user);
  const tiers = useSelector((state) => state.profile.tiers);
  const isPlanExpired = useSelector((state) => state.profile.isPlanExpired);
  const planExpirationDate = useSelector((state) => state.profile.user?.planExpirationDate);
  const hasVideoChatTime = (user ? Math.floor(user.maxVideoCallTime) - Math.floor(user.currentVideoCallUsage ?? 0) : 0) > 0;

  // Format user data from Redux state
  const userProfile = {
    name: user?.name || "User",
    email: user?.email || "",
    joinDate: user?.createdAt ? moment(user.createdAt).format("MMMM DD, YYYY") : "N/A",
  };

  // Tour highlight state
  const [showTour, setShowTour] = useState(false);
  const tourTimeoutRef = useRef();

  useEffect(() => {
    // Only show the tour if it hasn't been shown before
    if (showCancelTour && !localStorage.getItem('cancelTourShown')) {
      setShowTour(true);
      localStorage.setItem('cancelTourShown', '1');
      // Remove highlight after 8 seconds if not clicked
      tourTimeoutRef.current = setTimeout(() => setShowTour(false), 8000);
    }
    return () => clearTimeout(tourTimeoutRef.current);
  }, [showCancelTour]);

  const handleCancelClick = () => {
    setShowTour(false);
    dispatch(profileActions.setCancelPlanModalOpen(true));
  };

  return (
    <div className="w-full px-4 sm:px-0 sm:mx-0 sm:max-w-xl mt-8" style={{ animationDelay: "0.8s" }}>
      <div className="bg-gradient-to-b from-slate-950 to-fuchsia-950/80 rounded-xl p-8 border border-red-900/20 shadow-xl">
        {/* Subscription Management Section */}
        {tiers.length > 0 && (userCurrentTier || !isPlanExpired) && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-pink-200 mb-6">Your Subscription</h2>

            <div className="bg-slate-900/50 backdrop-blur-sm rounded-lg p-5 mb-6 border border-red-900/20">
              <div className="flex justify-between items-center mb-3">
                <span className="text-base text-pink-300/70">Current Plan:</span>
                <span className="text-base font-bold text-red-700">{tiers.find((tier) => tier._id === userCurrentTier)?.name || "Premium"}</span>
              </div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-base text-pink-300/70">Status:</span>
                <span className="text-base text-green-400">Active</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-base text-pink-300/70">{userCurrentTier ? "Next Billing:" : "Plan Expiration:"}</span>
                <span className="text-base text-white">{planExpirationDate ? moment(planExpirationDate).format("MMMM DD, YYYY") : "N/A"}</span>
              </div>
            </div>

            <h3 className="text-lg font-semibold mb-4 text-white">Premium Plan</h3>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center p-4 rounded-lg bg-gradient-to-r from-slate-900 to-fuchsia-950/80 border border-red-900/30">
                <div>
                  <h4 className="text-base text-pink-300">{tiers[0].name || "Ultimate"}</h4>
                  <p className="text-sm text-pink-300/70">Premium experience</p>
                  <ul className="mt-2 text-xs text-pink-300/80 space-y-1">
                    {tiers[0].description?.messaging && (
                      <li className="flex items-center">
                        <svg className="w-3 h-3 mr-1 text-pink-400" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          ></path>
                        </svg>
                        {tiers[0].description?.messaging}
                      </li>
                    )}
                    {tiers[0].description?.voiceCall && (
                      <li className="flex items-center">
                        <svg className="w-3 h-3 mr-1 text-pink-400" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          ></path>
                        </svg>
                        {tiers[0].description?.voiceCall}
                      </li>
                    )}
                    {tiers[0].description?.image && (
                      <li className="flex items-center">
                        <svg className="w-3 h-3 mr-1 text-pink-400" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          ></path>
                        </svg>
                        {tiers[0].description?.image}
                      </li>
                    )}
                    {tiers[0].description?.videoCall && (
                      <li className="flex items-center">
                        <svg className="w-3 h-3 mr-1 text-pink-400" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          ></path>
                        </svg>
                        {tiers[0].description?.videoCall}
                      </li>
                    )}
                  </ul>
                </div>
                <div className="text-right">
                  <p className="font-bold text-white">
                    ${tiers[0].price?.$numberDecimal || "199"}
                    /month
                  </p>
                  <p className="text-xs text-pink-300">Current Plan</p>
                </div>
              </div>
            </div>

            {/* <div className="flex justify-center mb-5 space-x-4">
              <button
                onClick={() => dispatch(profileActions.setCancelPlanModalOpen(true))}
                className="text-base px-6 py-2.5 text-white bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-full hover:opacity-90 transition-all shadow-md"
              >
                Cancel Plan
              </button>
            </div> */}

            <div className="text-center mt-4 mb-6" id="cancel-subscription" style={{ position: 'relative' }}>
              {userCurrentTier ? (
                <>
                  <button
                    onClick={handleCancelClick}
                    className={`text-sm text-pink-300/70 hover:text-pink-200 transition-all active:translate-y-0.5 active:shadow-none ${showTour ? 'ring-4 ring-pink-400 ring-opacity-60 animate-pulse' : ''}`}
                    style={showTour ? {
                      boxShadow: '0 0 0 6px #f472b6, 0 0 18px 6px #f472b6',
                      padding: '0.75rem 2.5rem',
                      borderRadius: '2rem',
                      zIndex: 2,
                    } : {}}
                  >
                    Cancel your subscription
                  </button>
                  {showTour && (
                    <div style={{
                      position: 'absolute',
                      left: '50%',
                      top: '110%',
                      transform: 'translateX(-50%)',
                      background: 'rgba(30, 41, 59, 0.95)',
                      color: '#f472b6',
                      padding: '0.75rem 1.25rem',
                      borderRadius: '0.75rem',
                      boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
                      zIndex: 10,
                      fontSize: '1rem',
                      marginTop: '0.5rem',
                      minWidth: '220px',
                    }}>
                      Click here to cancel your subscription.<br />
                      You can always reactivate later!
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center space-y-3">
                  <p className="text-pink-300/70">You have canceled your subscription. You're no longer subscribed to Premium.</p>
                  <button
                    onClick={() => dispatch(profileActions.setReactivatePlanModalOpen(true))}
                    className="text-base px-6 py-2.5 text-white bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-full hover:opacity-90 transition-all shadow-md active:translate-y-0.5 active:shadow-none"
                  >
                    Reactivate your subscription
                  </button>
                </div>
              )}
            </div>

            {/* Usage Status Section */}
            <div className={`bg-slate-900/50 backdrop-blur-sm rounded-lg p-5 mb-6 border border-red-900/20`}>
              <h3 className="text-lg font-semibold mb-4 text-white">Usage Status</h3>

              <div className="space-y-4">
                {/* Messaging */}
                <div className="flex justify-between items-center pb-2 border-b border-red-900/20">
                  <span className="text-base text-pink-300/70">Messaging:</span>
                  <span className="text-base text-red-700 font-medium">Unlimited</span>
                </div>

                {/* Phone Call Time */}
                <div className="flex justify-between items-center pb-2 border-b border-red-900/20">
                  <span className="text-base text-pink-300/70">Phone Call Time:</span>
                  <span className="text-base text-red-700 font-medium">Unlimited</span>
                </div>

                {/* Images */}
                <div className="flex justify-between items-center pb-2 border-b border-red-900/20">
                  <span className="text-base text-pink-300/70">Images:</span>
                  <span className="text-base text-red-700 font-medium">Unlimited</span>
                </div>

                {/* Video Chat Time with Progress Bar */}
                <VideoChatProgressComponent />

                {/* Plan Reset Date */}
                {/* <div className="flex justify-between items-center">
                    <span className="text-base text-pink-300/70">Plan resets on:</span>
                    <span className="text-base text-white">{moment().add(1, "month").format("MMMM D, YYYY")}</span>
                  </div> */}
              </div>

              {/* Add-ons Button */}
              <div className="mt-4 flex justify-center">
                {userCurrentTier && (
                  <button
                    onClick={() => dispatch(profileActions.setShowAddonsModal(true))}
                    className="text-base px-8 py-2 bg-slate-900/70 hover:bg-slate-800/80 rounded-full border border-red-900/20 text-white transition-all hover:shadow-red-900/20 hover:shadow-lg"
                  >
                    Add-ons
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-pink-200">Your Profile</h2>
          <div className="text-xs px-3 py-1 bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-full">
            {userCurrentTier ? "Premium Account" : "Free Account"}
          </div>
        </div>

        <div className="space-y-6">
          {Object.entries({
            Name: userProfile.name,
            Email: userProfile.email,
            // "Member Since": userProfile.joinDate,
          }).map(([key, value]) => (
            <div key={key} className="flex justify-between items-center border-b border-red-900/20 pb-4 max-sm:flex-col max-sm:items-start">
              <span className="text-base text-pink-300">{key}:</span>
              <span className="text-base font-medium text-white">{value}</span>
            </div>
          ))}
        </div>

        {hasVideoChatTime && !userCurrentTier && isPlanExpired && (
          <div className="py-6">
            <VideoChatProgressComponent />
          </div>
        )}

        {/* <div className="mt-6 flex justify-center space-x-4">
          <button
            onClick={() => dispatch(changePasswordActions.setShowChangePasswordModal(true))}
            className="text-base px-6 py-2.5 text-white bg-slate-900/70 border border-red-900/30 rounded-md hover:bg-red-900/40 transition-colors hover-scale max-sm:text-sm max-sm:px-4 max-sm:py-2"
          >
            Change Password
          </button>
          <button
            onClick={() => dispatch(profileActions.setEditMode(true))}
            className="text-base px-6 py-2.5 text-white bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-md hover:opacity-90 transition-all shadow-md hover-scale max-sm:text-sm max-sm:px-4 max-sm:py-2"
          >
            Edit Profile
          </button>
        </div> */}

        <div className="mt-6 flex justify-center space-x-4">
          <button
            onClick={() => dispatch(changePasswordActions.setShowChangePasswordModal(true))}
            className="text-base px-6 py-2.5 text-white bg-slate-900/70 border border-red-900/30 rounded-md hover:bg-red-900/40 transition-all max-sm:text-sm max-sm:px-4 max-sm:py-2 active:translate-y-0.5 active:shadow-none"
          >
            Change Password
          </button>
          <button
            onClick={() => dispatch(profileActions.setEditMode(true))}
            className="text-base px-6 py-2.5 text-white bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-md hover:opacity-90 transition-all shadow-md max-sm:text-sm max-sm:px-4 max-sm:py-2 active:translate-y-0.5 active:shadow-none"
          >
            Edit Profile
          </button>
        </div>
      </div>
    </div>
  );
}
